export interface ReportRecord {
    is_life: boolean,
    gip: string,
    gai: string,
    gip_ranking: number,
    gai_ranking: number,
    tier: null | number,
    relative_penetration: string,
    demand_elasticity: string,
    years_to_close_gap: null | number,
    report_year: number,
    country_code: string
}

export interface CountryRecord {
    report_year: number,
    country_code: string,
    group: string,
    subgroup: string,
    gdp: string,
    gdp_per_capita: string,
    population: number
}

export interface RegionRecord {
    report_year: number,
    region_code: string,
    bps: string,
    bps_proportion: string,
    is_life: boolean,
    area_group_code: string
}

export interface Report {
    id: string,
    year: number,
    download_urls: {
      es?: string,
      en?: string
    }
    country_records: CountryRecord[],
    region_records: RegionRecord[],
    records: ReportRecord[]
}

export type GipData = Report[];


const DataService = (() => {
    const STATIC_DATA_URL = '/gip/data/years.json';

    // Transform the static JSON data to match the expected format
    const formatAvailableReports = (reports: GipData) => {
        return reports.reduce((prev: any, item: Report) => {
            prev[item.year] = item;
            return prev;
        }, {});
    };

    const getYearsToFetch = (availableReports: any, yearsToFetch: number) => {
        const years = Object.keys(availableReports).map(year => Number(year));
        return years.sort().slice(-yearsToFetch);
    };

    const formatBps = (last10YearFullReports: any) => {
        const bpsData = Object.keys(last10YearFullReports).map(key => {
            return last10YearFullReports[key]["region_records"].map((a: any) => {
                return { 
                    region: a.region_code, 
                    year: a.report_year, 
                    bps: a.bps, 
                    bps_proportion: a.bps_proportion, 
                    is_life: a.is_life, 
                    area: a.area_group_code
                };
            });
        }).flat();

        return bpsData;
    };

    const fetchInitialData = async (): Promise<[any, any, number | null, any]> => {
        try {
            const response = await fetch(STATIC_DATA_URL);
            const gipData: GipData = await response.json();

            // Format all reports by year
            const availableReports = formatAvailableReports(gipData);
            
            // Get last 11 years
            const YEARS_TO_FETCH = 11;
            const lastYears = getYearsToFetch(availableReports, YEARS_TO_FETCH);
            
            // Extract last 11 years of full reports
            const last10YearFullReports = lastYears.reduce((prev: any, year: number) => {
                prev[year] = availableReports[year];
                return prev;
            }, {});
            
            // Get last available year (single number, not array)
            const lastYearAvailableArray = getYearsToFetch(availableReports, 1);
            const lastYearAvailable = lastYearAvailableArray.length > 0 ? lastYearAvailableArray[0] : null;
            
            // Format BPS data
            const bpsData = formatBps(last10YearFullReports);

            return [availableReports, last10YearFullReports, lastYearAvailable, bpsData];
        } catch (err) {
            console.error('Error loading static data:', err);
            return [null, null, null, null];
        }
    };

    return {
        fetchInitialData: () => fetchInitialData()
    }
})();

export default DataService
